import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import type {
  TrainingScenario,
  TrainingSession,
  TrainingStats,
  CoachingGoal,
} from "../types/training";

const ENDPOINTS = {
  scenarios: "/training/scenarios",
  sessions: "/training/sessions",
  stats: "/training/stats",
  goals: "/training/goals",
  callInsights: "/training/call-insights",
  myCalls: "/training/my-calls",
  allowance: "/training/allowance",
  roster: "/training/agency/roster",
  agencyAssignments: "/training/agency/assignments",
  myAssignments: "/training/assignments",
  certifications: "/training/certifications",
  ramp: "/training/agency/ramp",
  leaderboard: "/training/leaderboard",
  best: "/training/agency/best",
  examples: "/training/examples",
  setExample: "/training/agency/examples",
  quiz: "/training/quiz",
  paths: "/training/agency/paths",
};

/** Free minutes, wallet and this month's bill — the meter on the InsuraMentor page. */
export interface BillingSummary {
  freeMinutes: number;
  usedMinutes: number;
  remainingFreeMinutes: number;
  costPerMinuteCents: number;
  planName: string | null;
  walletBalanceCents: number;
  walletMinutes: number;
  thisMonth: { sessions: number; minutes: number; chargedCents: number; refundedCents: number };
}

/** One of the agent's own recorded calls, for the "From my calls" door. */
export interface MyCall {
  id: string;
  createdAt: string;
  duration: number | null;
  callStatus: string | null;
  recordingUrl: string;
  leadName: string | null;
  toNumber: string | null;
  transcriptStatus: "processing" | "completed" | "failed" | null;
}

export interface CallPerformance {
  userId: string;
  hasEnoughData: boolean;
  window: { days: number };
  calls: {
    dialed: number;
    connected: number;
    talkTimeSeconds: number;
    medianConnectedSeconds: number;
    openerFailRate: number;
  };
  appointments: {
    set: number;
    kept: number;
    noShow: number;
    noShowRate: number;
    setRate: number;
  };
  outcomes: {
    sold: number;
    approved: number;
    declined: number;
    pending: number;
    closeRate: number;
    closePerKept: number;
    approvalRate: number;
  };
  weakArea: string | null;
  reason: string;
  recommendedCategories: string[];
}

export interface RosterRow {
  userId: string;
  name: string;
  email: string;
  role: string;
  performance: CallPerformance;
  training: {
    sessions: number;
    avgScore: number | null;
    lastSessionAt: string | null;
    dimensions: Record<string, number>;
    weakest: { key: string; label: string; score: number } | null;
    /** Roleplays where eligibility came up, and how many were right against the carrier rules. */
    underwriting?: { checked: number; correct: number };
  };
  agencyId: string;
  agencyName: string;
  openAssignments: number;
  certifications?: Certification[];
  /** Underwriting drills: categories passed (a pass = the scorer's verdict was 'correct'). */
  knowledge?: { attempts: number; passed: number; total: number };
  trained: Array<{ scenarioId: string; name: string; score: number | null; at: string }>;
  suggestion: { scenarioId: string; scenarioName: string; reason: string } | null;
}

export interface RosterResult {
  agencies: Array<{ id: string; name: string; depth: number }>;
  rows: RosterRow[];
}

export interface Certification { pathId: string; name: string; unlocks: string | null; certifiedAt?: string }
export interface RampCohort {
  month: string; hired: number; sold: number;
  medianDays: number | null; medianDaysPractised: number | null; medianDaysNotPractised: number | null; practisedCount: number;
}
export interface RampAgent { userId: string; name: string; joinedAt: string; firstSaleAt: string | null; daysToFirstSale: number | null; practisedFirst: boolean }
export interface LeaderboardRow { rank: number; userId: string; name: string; sessions: number; avgScore: number | null; bestScore: number | null; closes: number }
export interface BestSession { dim: string; sessionId: string; userId: string; name: string; scenario: string; score: number | null; at: string; isExample: boolean; hasRecording: boolean }
export interface PeerExample {
  sessionId: string; firstName: string; scenario: string; score: number | null; note: string | null; at: string;
  objectionHandling: number | null; discovery: number | null; compliance: number | null; closing: number | null; rapport: number | null;
}
export interface DrillCategory { key: string; label: string; attempts: number; passed: number; lastVerdict: string | null; lastNote: string | null; lastAt: string | null }
export interface UnderwritingProgress { categories: DrillCategory[]; passedCount: number; total: number }
export interface QuizQuestion { id: string; kind: string; prompt: string; options: string[]; carrier: string; condition: string; category: string }
export interface QuizStart { id: string; questions: QuizQuestion[]; total: number }
export interface QuizResult {
  id: string; score: number | null; total: number; answers: Array<number | null>;
  questions: Array<QuizQuestion & { correctIndex: number; explanation: string }>;
  completedAt: string | null;
}
export interface QuizHistoryRow { id: string; score: number | null; total: number; category: string | null; completedAt: string | null; createdAt: string }

export interface TrainingAssignment {
  id: string;
  scenarioId: string;
  assignedToUserId: string;
  reason?: string | null;
  weakArea?: string | null;
  note?: string | null;
  dueAt?: string | null;
  status: string;
  createdAt: string;
  scenario?: TrainingScenario;
}

export const trainingApi = {
  // Scenarios
  getScenarios: async (): Promise<TrainingScenario[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.scenarios) });
    return data.data;
  },

  getScenario: async (id: string): Promise<TrainingScenario> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.scenarios}/${id}`) });
    return data.data;
  },

  createScenario: async (body: Partial<TrainingScenario>): Promise<TrainingScenario> => {
    const { data } = await api.post<any>({ url: getApiUrl(ENDPOINTS.scenarios), body });
    return data.data;
  },

  generateScenario: async (characterId?: string): Promise<TrainingScenario> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.scenarios}/generate`), body: { characterId } });
    return data.data;
  },

  /**
   * Describe a prospect in plain English and get a persona.
   *
   * Unlike generateScenario() this is NOT gated on having completed a session —
   * it is the path that works on an agent's first day, when they have no score
   * to adapt to but know exactly what keeps beating them.
   */
  createFromDescription: async (body: {
    characterId?: string;
    description: string;
    productType?: string;
    difficulty?: string;
    shareWithAgency?: boolean;
  }): Promise<TrainingScenario> => {
    const { data } = await api.post<any>({
      url: getApiUrl(`${ENDPOINTS.scenarios}/from-description`),
      body,
    });
    return data.data;
  },

  /** Build the prospect one of the agent's own call scripts is written for. */
  createFromScript: async (body: { scriptId: string; characterId?: string; shareWithAgency?: boolean; difficulty?: string }): Promise<TrainingScenario> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.scenarios}/from-script`), body });
    return data.data;
  },
  /** Build a persona from the agent's OWN recorded calls (max 5). */
  createFromCalls: async (body: {
    characterId?: string;
    callIds: string[];
    productType?: string;
    difficulty?: string;
    shareWithAgency?: boolean;
  }): Promise<TrainingScenario> => {
    const { data } = await api.post<any>({
      url: getApiUrl(`${ENDPOINTS.scenarios}/from-calls`),
      body,
    });
    return data.data;
  },

  // Sessions
  startSession: async (scenarioId: string): Promise<{ session: TrainingSession; scenario: TrainingScenario }> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.sessions}/start`), body: { scenarioId } });
    return data.data;
  },

  endSession: async (sessionId: string): Promise<TrainingSession> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.sessions}/${sessionId}/end`) });
    return data.data;
  },

  getSession: async (sessionId: string): Promise<TrainingSession> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.sessions}/${sessionId}`) });
    return data.data;
  },

  saveScore: async (sessionId: string, scores: Record<string, any>): Promise<TrainingSession> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.sessions}/${sessionId}/score`), body: scores });
    return data.data;
  },

  autoScore: async (sessionId: string): Promise<TrainingSession> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.sessions}/${sessionId}/auto-score`) });
    return data.data;
  },

  getSessions: async (limit = 20, offset = 0): Promise<{ rows: TrainingSession[]; count: number }> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.sessions}?limit=${limit}&offset=${offset}`) });
    return data.data;
  },

  // Stats
  getStats: async (): Promise<TrainingStats> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.stats) });
    return data.data;
  },

  // Goals
  getGoals: async (): Promise<CoachingGoal[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.goals) });
    return data.data;
  },

  createGoal: async (body: Partial<CoachingGoal>): Promise<CoachingGoal> => {
    const { data } = await api.post<any>({ url: getApiUrl(ENDPOINTS.goals), body });
    return data.data;
  },

  updateGoalProgress: async (goalId: string, value: number): Promise<CoachingGoal> => {
    const { data } = await api.put<any>({ url: getApiUrl(`${ENDPOINTS.goals}/${goalId}/progress`), body: { value } });
    return data.data;
  },

  // Preferences (Onboarding)
  getPreferences: async (): Promise<any> => {
    const { data } = await api.get<any>({ url: getApiUrl("/training/preferences") });
    return data.data;
  },

  savePreferences: async (prefs: { products: string[]; experience: string; weakAreas: string[] }): Promise<any> => {
    const { data } = await api.post<any>({ url: getApiUrl("/training/preferences"), body: prefs });
    return data.data;
  },

  // ── Real-call signals, roster, assignments ─────────────────────────────
  getBilling: async (): Promise<BillingSummary> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.allowance) });
    return data.data;
  },
  /** Owner/admin: give a session's charge back to the agent's wallet. */
  refundSession: async (sessionId: string, note?: string): Promise<TrainingSession> => {
    const { data } = await api.post<any>({ url: getApiUrl(`/training/sessions/${sessionId}/refund`), body: { note } });
    return data.data;
  },
  getMyCalls: async (limit = 50): Promise<MyCall[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.myCalls}?limit=${limit}`) });
    return data.data;
  },
  getCallInsights: async (days = 90): Promise<CallPerformance> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.callInsights}?days=${days}`) });
    return data.data;
  },

  getRoster: async (days = 90): Promise<RosterResult> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.roster}?days=${days}`) });
    return data.data;
  },

  getMyAssignments: async (): Promise<TrainingAssignment[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.myAssignments) });
    return data.data;
  },

  assignTraining: async (body: {
    assignedToUserId: string;
    scenarioId: string;
    reason?: string;
    weakArea?: string;
    note?: string;
    dueAt?: string;
  }): Promise<TrainingAssignment> => {
    const { data } = await api.post<any>({ url: getApiUrl(ENDPOINTS.agencyAssignments), body });
    return data.data;
  },

  cancelAssignment: async (id: string): Promise<void> => {
    await api.delete<any>({ url: getApiUrl(`${ENDPOINTS.agencyAssignments}/${id}`) });
  },

  // ── Round two ────────────────────────────────────────────────────────
  getCertifications: async (): Promise<Certification[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.certifications) });
    return data.data;
  },
  getRamp: async (months = 6): Promise<{ cohorts: RampCohort[]; agents: RampAgent[] }> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.ramp}?months=${months}`) });
    return data.data;
  },
  getLeaderboard: async (days = 30): Promise<LeaderboardRow[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.leaderboard}?days=${days}`) });
    return data.data;
  },
  getBestSessions: async (): Promise<BestSession[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.best) });
    return data.data;
  },
  setExample: async (sessionId: string, isExample: boolean, note?: string): Promise<void> => {
    await api.post<any>({ url: getApiUrl(`${ENDPOINTS.setExample}/${sessionId}`), body: { isExample, note } });
  },
  getExamples: async (): Promise<PeerExample[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.examples) });
    return data.data;
  },
  /** Build a prospect with real conditions from the chosen category; returns the scenario to start. */
  createUnderwritingDrill: async (body: { category?: string; characterId?: string } = {}): Promise<TrainingScenario> => {
    const { data } = await api.post<any>({ url: getApiUrl("/training/underwriting/drill"), body });
    return data.data;
  },
  getUnderwritingProgress: async (): Promise<UnderwritingProgress> => {
    const { data } = await api.get<any>({ url: getApiUrl("/training/underwriting/progress") });
    return data.data;
  },
  startQuiz: async (body: { count?: number; category?: string } = {}): Promise<QuizStart> => {
    const { data } = await api.post<any>({ url: getApiUrl(ENDPOINTS.quiz), body });
    return data.data;
  },
  submitQuiz: async (id: string, answers: Array<number | null>): Promise<QuizResult> => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.quiz}/${id}/submit`), body: { answers } });
    return data.data;
  },
  getQuizHistory: async (): Promise<QuizHistoryRow[]> => {
    const { data } = await api.get<any>({ url: getApiUrl(`${ENDPOINTS.quiz}/history`) });
    return data.data;
  },
  createPath: async (body: { name: string; description?: string; steps: Array<{ scenarioId: string; passScore: number }>; isCertification?: boolean; unlocks?: string | null }) => {
    const { data } = await api.post<any>({ url: getApiUrl(ENDPOINTS.paths), body });
    return data.data;
  },
  getPaths: async () => {
    const { data } = await api.get<any>({ url: getApiUrl(ENDPOINTS.paths) });
    return data.data;
  },
  enrollInPath: async (pathId: string, assignedToUserId: string) => {
    const { data } = await api.post<any>({ url: getApiUrl(`${ENDPOINTS.paths}/${pathId}/enroll`), body: { assignedToUserId } });
    return data.data;
  },
};
